// Multiresource groups in the hourly day calendars (admin Kalenteri, cashier calendar).
//
// A multiresource (customers/{cid}/resources, type 'multiresource', resources: [courtIds]) is what the
// customer app shows and sells as ONE bookable item; falcon-a ui.js gives clients each group with its
// active, ordinary, de-duplicated members and drops groups with fewer than 2. A booking on it is N
// reservations on N member courts sharing `multibookingid` (and carrying `multiresourceid`).
//
// The calendars mirror that: a collapsed group is one column holding its members' reservations (view
// only), and its header can expand it into the individual courts. Groups start collapsed; the expanded
// ones are remembered per browser. See docs/features/multiresources.md.
//
// Keep this file free of React and Firebase.

const byOrderThenName = (a, b) =>
  (a.order || 1000) - (b.order || 1000) || String(a.displayName || '').localeCompare(String(b.displayName || ''))

// The groups the calendars show: not deleted, members that exist among the hourly `resources`
// (de-duplicated, in stored order), at least 2 of them. A court belongs to only the first group that
// claims it (order, then name); a later group left with fewer than 2 unclaimed members is dropped.
export const activeGroups = (multiresources, resources) => {
  const known = new Set((resources || []).map((r) => r.id))
  const claimed = new Set()
  const out = []
  const candidates = (multiresources || [])
  candidates
    .filter((m) => m && m.id && m.state !== 'deleted' && Array.isArray(m.resources))
    .slice()
    .sort(byOrderThenName)
    .forEach((m) => {
      const members = []
      m.resources.forEach((id) => {
        if (known.has(id) && !claimed.has(id) && members.indexOf(id) === -1) members.push(id)
      })
      if (members.length < 2) return
      members.forEach((id) => claimed.add(id))
      out.push({ id: m.id, displayName: m.displayName || 'Ryhmä', order: m.order, members })
    })
  return out
}

// The calendar's columns from the visible courts (already filtered and sorted by the calendar).
// A collapsed group with at least 2 visible members becomes one column at the place of its first
// visible member: { id, displayName, isGroup: true, members: [visible member ids], memberNames }.
// Courts of an expanded group stay, tagged { groupId, groupName } so their header can collapse it.
export const buildColumns = (visibleResources, groups, expandedIds) => {
  const visible = visibleResources || []
  const expanded = new Set(expandedIds || [])
  const visibleIds = new Set(visible.map((r) => r.id))
  const groupOf = {}
  const collapsed = {}
  const allGroups = groups || []
  allGroups.forEach((g) => {
    const shown = g.members.filter((id) => visibleIds.has(id))
    if (expanded.has(g.id) || shown.length < 2) {
      shown.forEach((id) => { groupOf[id] = g })
    } else {
      collapsed[g.id] = { ...g, members: shown }
      shown.forEach((id) => { groupOf[id] = collapsed[g.id] })
    }
  })
  const nameOf = {}
  visible.forEach((r) => { nameOf[r.id] = r.displayName })
  const out = []
  const placed = new Set()
  visible.forEach((r) => {
    const g = groupOf[r.id]
    if (g && collapsed[g.id] === g) {
      if (placed.has(g.id)) return
      placed.add(g.id)
      out.push({
        id: g.id,
        displayName: g.displayName,
        isGroup: true,
        members: g.members,
        memberNames: g.members.map((id) => nameOf[id] || id),
      })
    } else if (g) {
      out.push({ ...r, groupId: g.id, groupName: g.displayName })
    } else {
      out.push(r)
    }
  })
  return out
}

// memberId -> collapsed group column, for the columns buildColumns returned.
const collapsedIndex = (columns) => {
  const index = {}
  const cols = columns || []
  cols.forEach((c) => {
    if (c.isGroup) c.members.forEach((id) => { index[id] = c })
  })
  return index
}

export const isGroupColumn = (columns, resourceId) => (columns || []).some((c) => c.isGroup && c.id === resourceId)

const multibookingidOf = (event) =>
  (event && event.data && event.data.multibookingid) || (event && event.multibookingid) || null

const isFreeSlot = (event) => !!event && !!event.data && event.data.state === 'open'

const memberName = (group, id) => {
  const i = group.members.indexOf(id)
  return i > -1 && group.memberNames ? group.memberNames[i] : id
}

// The events for the calendar. Events of a collapsed group's members move to the group column
// (memberResourceId keeps the court) and are view only (groupView: true):
// - the reservations of one merged booking (same multibookingid) become one event
//   "{title} × N ({court names})";
// - any other reservation keeps its own event, its title prefixed with the court name;
// - free slots are dropped (nothing can be booked from a collapsed column).
// baseTitle(event) gives the title to start from (the calendar may build it from the payer).
export const groupEvents = (events, columns, baseTitle) => {
  const index = collapsedIndex(columns)
  const titleOf = baseTitle || ((e) => e.title || '')
  const out = []
  const merged = {}
  const all = events || []
  all.forEach((e) => {
    const group = e && index[e.resourceId]
    if (!group) {
      out.push(e)
      return
    }
    if (isFreeSlot(e)) return
    const court = memberName(group, e.resourceId)
    const mbid = multibookingidOf(e)
    if (mbid) {
      const key = group.id + '|' + mbid
      if (merged[key]) {
        merged[key].memberResourceIds.push(e.resourceId)
        merged[key].memberNames.push(court)
        return
      }
      merged[key] = { ...e, resourceId: group.id, memberResourceId: e.resourceId, memberResourceIds: [e.resourceId], memberNames: [court], groupView: true, baseTitle: titleOf(e) }
      out.push(merged[key])
      return
    }
    out.push({ ...e, resourceId: group.id, memberResourceId: e.resourceId, groupView: true, title: court + ': ' + titleOf(e) })
  })
  // titles of merged bookings once all their courts are known
  Object.keys(merged).forEach((key) => {
    const m = merged[key]
    m.title = m.memberNames.length > 1
      ? m.baseTitle + ' × ' + m.memberNames.length + ' (' + m.memberNames.join(', ') + ')'
      : m.memberNames[0] + ': ' + m.baseTitle
    delete m.baseTitle
  })
  return out
}

// Background events (opening hours, closed times) of a collapsed group's members: one per distinct
// interval in the group column. Events without a resourceId apply to every column and stay as they are.
export const groupBackgroundEvents = (events, columns) => {
  const index = collapsedIndex(columns)
  const seen = new Set()
  const out = []
  const all = events || []
  all.forEach((e) => {
    const group = e && index[e.resourceId]
    if (!group) {
      out.push(e)
      return
    }
    const key = group.id + '|' + e.start + '|' + e.end + '|' + (e.title || '')
    if (seen.has(key)) return
    seen.add(key)
    out.push({ ...e, resourceId: group.id, memberResourceId: e.resourceId })
  })
  return out
}

// Per-browser memory of the expanded groups. Every access tolerates a missing or throwing storage
// (private mode, blocked site data): then everything is simply collapsed.
export const expandedGroupsStore = (customerid, storage) => {
  const key = 'calendarExpandedGroups:' + (customerid || 'default')
  const store = () => {
    try {
      return storage || (typeof window !== 'undefined' ? window.localStorage : null)
    } catch (e) {
      return null
    }
  }
  return {
    load: () => {
      try {
        const s = store()
        const raw = s ? s.getItem(key) : null
        const parsed = raw ? JSON.parse(raw) : []
        return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : []
      } catch (e) {
        return []
      }
    },
    save: (ids) => {
      try {
        const s = store()
        if (s) s.setItem(key, JSON.stringify(ids || []))
      } catch (e) {
        // remembered for this session only
      }
    },
  }
}
